import type { HTMLElement } from 'node-html-parser/dist';

interface Istore {
	elements: {
		[key: string]: {
			classList: string[];
		};
	};
}

/**
 * Interface for the ClientServerBridge class
 * All getters read from the store in case of Server and from the DOM in case of client.
 */
interface IClientServerBridge {
	/**
	 * Stores the data needed required for supporting various APIs
	 */
	store: Istore;

	/**
	 *
	 * @param parsedHTML - HTMLElement received from Jira request to tesseract
	 * Server: Sets the documentRef to the intial HTML received from Jira request to tesseract HTMLElement
	 * Client: Sets the documentRef to document
	 *
	 * It will be called only in entry script of SnapVM runtime
	 */
	startup(parsedHTML: HTMLElement): void;

	/**
	 * Returns the element with the given ID.
	 */
	getElementById(elementId: string): HTMLElement | Element | null;
	/**
	 * Returns the element with the given selector.
	 */
	querySelector(selector: string): HTMLElement | Element | null;
	/**
	 * Returns all elements that match the given selector.
	 */
	querySelectorAll(selector: string): HTMLElement[] | NodeListOf<Element>;
	/**
	 * Returns the element with the given tag name.
	 */
	getElementsByTagName(tagName: string): HTMLCollectionOf<Element> | HTMLElement[];
	/**
	 * To add class names to the element with the given name.
	 * CLIENT: Gets added to the DOM directly
	 * SERVER: Gets added to the store
	 */
	addClassName(elementName: string, className: string): void;
	/**
	 * Returns the store object
	 */
	getStore(): Istore;
}

/**
 * This class is used to abstract the differences between the client and server environments.
 */
export class ClientServerBridge implements IClientServerBridge {
	store: Istore = {
		elements: {},
	};
	parsedHTML: HTMLElement | null;

	constructor() {
		this.parsedHTML = null;
	}

	startup(parsedHTML: HTMLElement): void {
		this.parsedHTML = parsedHTML;
	}

	isRunningInSnapvmRuntime(): boolean {
		return typeof window === 'undefined';
	}

	getDataRef(): HTMLElement | Document | null {
		if (this.isRunningInSnapvmRuntime()) {
			return this.parsedHTML;
		}
		// Set to document when not in SnapVM runtime (Classic and Client side render cases)
		// Upgrading @atlaskit/platform; fix these violations.
		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		return document;
	}

	getElementById(elementId: string): HTMLElement | Element | null {
		const documentRef = this.getDataRef();
		if (documentRef) {
			return documentRef.getElementById(elementId);
		}
		// oxlint-disable-next-line no-console
		console.warn('documentRef is null in getElementById');
		return null;
	}

	querySelector(selector: string): HTMLElement | Element | null {
		const documentRef = this.getDataRef();
		if (documentRef) {
			return documentRef.querySelector(selector);
		}
		// oxlint-disable-next-line no-console
		console.warn('documentRef is null in querySelector');
		return null;
	}

	querySelectorAll(selector: string): HTMLElement[] | NodeListOf<Element> {
		const documentRef = this.getDataRef();
		if (documentRef) {
			return documentRef.querySelectorAll(selector);
		}
		// oxlint-disable-next-line no-console
		console.warn('documentRef is null in querySelectorAll');
		return [];
	}

	getElementsByTagName(tagName: string): HTMLCollectionOf<Element> | HTMLElement[] {
		const documentRef = this.getDataRef();
		if (documentRef) {
			return documentRef.getElementsByTagName(tagName);
		}
		// oxlint-disable-next-line no-console
		console.warn('documentRef is null in getElementsByTagName');
		return [];
	}

	addClassName(elementName: string, className: string): void {
		if (this.isRunningInSnapvmRuntime()) {
			if (!this.store.elements[elementName]) {
				this.store.elements[elementName] = {
					classList: [],
				};
			}
			this.store.elements[elementName].classList.push(className);
			return;
		}

		if (elementName === 'body') {
			// Upgrading @atlaskit/platform; fix these violations.
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			document.body.classList.add(className);
			return;
		}
		// oxlint-disable-next-line no-console
		console.warn('invalid elementName sent to addClassName. elementName: ', elementName);
	}

	getStore() {
		return this.store;
	}
}

const clientServerBridge = new ClientServerBridge();
export default clientServerBridge;
